/**
 * WMS Advanced Quality Management Types (Phase 4)
 * Shared between backend-wms, web-wms, and mobile-app
 *
 * Includes types for:
 * - Inventory Holds & Release Workflow
 * - Certificate of Analysis (CoA)
 * - QC Checkpoint Types
 * - QC Inspection Disposition
 * - AQL Inspection Levels
 *
 * Note: QCRuleType, QCMethod, QCInspectionStatus, QCInspectionResult, QCCheckpointResult
 * are already defined in inbound.types.ts
 *
 * DRY Compliance: Single source for all quality management enums
 * Rule 15 Compliance: Proper type definitions, no workarounds
 */

// ============================================================================
// QC EXECUTION MODE
// ============================================================================

/**
 * How a matched QC rule is executed at receipt (F2a).
 *
 * - `INLINE` — the receiver quality-checks the goods in the same flow (dock-to-
 *   stock / low-risk). This is the rollout default: a NULL rule/warehouse mode
 *   resolves to INLINE (see `resolveExecutionMode`).
 * - `SEGREGATED` — receiver ≠ inspector: goods route to a QC-hold location, a
 *   separate inspector passes/fails them later (GMP / regulated goods).
 *
 * varchar-backed (not a pg enum) to match this module's `on_failure` /
 * `rule_type` / `qc_method` convention and avoid enumName drift.
 */
export enum QcExecutionMode {
  INLINE = 'INLINE',
  SEGREGATED = 'SEGREGATED',
}

// ============================================================================
// INVENTORY HOLD ENUMS
// ============================================================================

/**
 * Type of entity being held
 */
export enum HoldType {
  INVENTORY_ITEM = 'INVENTORY_ITEM',
  LOT = 'LOT',
  RECEIVING_ORDER = 'RECEIVING_ORDER',
  PRODUCT = 'PRODUCT',
}

/**
 * Reason for placing an inventory hold
 */
export enum HoldReason {
  QC_FAILURE = 'QC_FAILURE',
  /** Goods parked in QC hold awaiting a separate inspector (segregated receiving, F2b). */
  PENDING_INSPECTION = 'PENDING_INSPECTION',
  PENDING_COA = 'PENDING_COA',
  CUSTOMER_COMPLAINT = 'CUSTOMER_COMPLAINT',
  RECALL = 'RECALL',
  REGULATORY = 'REGULATORY',
  DAMAGE = 'DAMAGE',
  OTHER = 'OTHER',
}

/**
 * Status of an inventory hold
 */
export enum HoldStatus {
  ACTIVE = 'ACTIVE',
  RELEASED = 'RELEASED',
  PARTIALLY_RELEASED = 'PARTIALLY_RELEASED',
  EXPIRED = 'EXPIRED',
  CANCELLED = 'CANCELLED',
}

/**
 * Actions recorded in hold history audit trail
 */
export enum HoldAction {
  CREATED = 'CREATED',
  EXTENDED = 'EXTENDED',
  PARTIALLY_RELEASED = 'PARTIALLY_RELEASED',
  RELEASED = 'RELEASED',
  CANCELLED = 'CANCELLED',
  EXPIRED = 'EXPIRED',
  NOTE_ADDED = 'NOTE_ADDED',
}

/**
 * Disposition when releasing a hold
 */
export enum HoldDisposition {
  RETURN_TO_AVAILABLE = 'RETURN_TO_AVAILABLE',
  SCRAP = 'SCRAP',
  RETURN_TO_VENDOR = 'RETURN_TO_VENDOR',
  REWORK = 'REWORK',
}

// ============================================================================
// QC INSPECTION ENUMS
// ============================================================================

/**
 * Disposition decision after QC inspection completion
 */
export enum QCInspectionDisposition {
  ACCEPT = 'ACCEPT',
  REJECT = 'REJECT',
  CONDITIONAL_ACCEPT = 'CONDITIONAL_ACCEPT',
  REWORK = 'REWORK',
  HOLD = 'HOLD',
}

/**
 * Types of QC checkpoints within an inspection
 */
export enum CheckpointType {
  PASS_FAIL = 'PASS_FAIL',
  MEASUREMENT = 'MEASUREMENT',
  VISUAL = 'VISUAL',
  COUNT = 'COUNT',
}

// ============================================================================
// CERTIFICATE OF ANALYSIS ENUMS
// ============================================================================

/**
 * Status of a Certificate of Analysis document
 */
export enum CoaStatus {
  PENDING = 'PENDING',
  VERIFIED = 'VERIFIED',
  REJECTED = 'REJECTED',
  EXPIRED = 'EXPIRED',
}

// ============================================================================
// AQL ENUMS
// ============================================================================

/**
 * AQL Inspection Levels (ANSI/ASQ Z1.4)
 */
export enum AqlInspectionLevel {
  GENERAL_1 = 'GENERAL_1',
  GENERAL_2 = 'GENERAL_2',
  GENERAL_3 = 'GENERAL_3',
  SPECIAL_1 = 'SPECIAL_1',
  SPECIAL_2 = 'SPECIAL_2',
  SPECIAL_3 = 'SPECIAL_3',
  SPECIAL_4 = 'SPECIAL_4',
}

// ============================================================================
// UI LABELS & COLORS
// ============================================================================

export const HoldTypeLabels: Record<HoldType, string> = {
  [HoldType.INVENTORY_ITEM]: 'Inventory Item',
  [HoldType.LOT]: 'Lot',
  [HoldType.RECEIVING_ORDER]: 'Receiving Order',
  [HoldType.PRODUCT]: 'Product',
};

export const HoldReasonLabels: Record<HoldReason, string> = {
  [HoldReason.QC_FAILURE]: 'QC Failure',
  [HoldReason.PENDING_INSPECTION]: 'Pending Inspection',
  [HoldReason.PENDING_COA]: 'Pending CoA',
  [HoldReason.CUSTOMER_COMPLAINT]: 'Customer Complaint',
  [HoldReason.RECALL]: 'Recall',
  [HoldReason.REGULATORY]: 'Regulatory',
  [HoldReason.DAMAGE]: 'Damage',
  [HoldReason.OTHER]: 'Other',
};

export const HoldReasonColors: Record<HoldReason, string> = {
  [HoldReason.QC_FAILURE]: 'red',
  [HoldReason.PENDING_INSPECTION]: 'blue',
  [HoldReason.PENDING_COA]: 'yellow',
  [HoldReason.CUSTOMER_COMPLAINT]: 'orange',
  [HoldReason.RECALL]: 'red',
  [HoldReason.REGULATORY]: 'purple',
  [HoldReason.DAMAGE]: 'orange',
  [HoldReason.OTHER]: 'gray',
};

export const HoldStatusLabels: Record<HoldStatus, string> = {
  [HoldStatus.ACTIVE]: 'Active',
  [HoldStatus.RELEASED]: 'Released',
  [HoldStatus.PARTIALLY_RELEASED]: 'Partially Released',
  [HoldStatus.EXPIRED]: 'Expired',
  [HoldStatus.CANCELLED]: 'Cancelled',
};

export const HoldStatusColors: Record<HoldStatus, string> = {
  [HoldStatus.ACTIVE]: 'red',
  [HoldStatus.RELEASED]: 'green',
  [HoldStatus.PARTIALLY_RELEASED]: 'yellow',
  [HoldStatus.EXPIRED]: 'gray',
  [HoldStatus.CANCELLED]: 'gray',
};

export const HoldDispositionLabels: Record<HoldDisposition, string> = {
  [HoldDisposition.RETURN_TO_AVAILABLE]: 'Return to Available',
  [HoldDisposition.SCRAP]: 'Scrap',
  [HoldDisposition.RETURN_TO_VENDOR]: 'Return to Vendor',
  [HoldDisposition.REWORK]: 'Rework',
};

export const HoldDispositionColors: Record<HoldDisposition, string> = {
  [HoldDisposition.RETURN_TO_AVAILABLE]: 'green',
  [HoldDisposition.SCRAP]: 'red',
  [HoldDisposition.RETURN_TO_VENDOR]: 'purple',
  [HoldDisposition.REWORK]: 'blue',
};

export const QCInspectionDispositionLabels: Record<QCInspectionDisposition, string> = {
  [QCInspectionDisposition.ACCEPT]: 'Accept',
  [QCInspectionDisposition.REJECT]: 'Reject',
  [QCInspectionDisposition.CONDITIONAL_ACCEPT]: 'Conditional Accept',
  [QCInspectionDisposition.REWORK]: 'Rework',
  [QCInspectionDisposition.HOLD]: 'Hold',
};

export const QCInspectionDispositionColors: Record<QCInspectionDisposition, string> = {
  [QCInspectionDisposition.ACCEPT]: 'green',
  [QCInspectionDisposition.REJECT]: 'red',
  [QCInspectionDisposition.CONDITIONAL_ACCEPT]: 'yellow',
  [QCInspectionDisposition.REWORK]: 'blue',
  [QCInspectionDisposition.HOLD]: 'orange',
};

export const QCInspectionStatusLabels: Record<string, string> = {
  PENDING: 'Pending',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  ON_HOLD: 'On Hold',
};

export const QCInspectionStatusColors: Record<string, string> = {
  PENDING: 'gray',
  IN_PROGRESS: 'blue',
  COMPLETED: 'green',
  CANCELLED: 'red',
  ON_HOLD: 'orange',
};

export const QCRuleTypeLabels: Record<string, string> = {
  BY_SUPPLIER: 'By Supplier',
  BY_PRODUCT: 'By Product',
  BY_CATEGORY: 'By Category',
  BY_ORIGIN: 'By Origin',
  BY_VALUE: 'By Value',
  BY_QUANTITY: 'By Quantity',
  CUSTOM: 'Custom',
};

export const CoaStatusLabels: Record<CoaStatus, string> = {
  [CoaStatus.PENDING]: 'Pending',
  [CoaStatus.VERIFIED]: 'Verified',
  [CoaStatus.REJECTED]: 'Rejected',
  [CoaStatus.EXPIRED]: 'Expired',
};

export const CoaStatusColors: Record<CoaStatus, string> = {
  [CoaStatus.PENDING]: 'gray',
  [CoaStatus.VERIFIED]: 'green',
  [CoaStatus.REJECTED]: 'red',
  [CoaStatus.EXPIRED]: 'orange',
};

export const CheckpointTypeLabels: Record<CheckpointType, string> = {
  [CheckpointType.PASS_FAIL]: 'Pass/Fail',
  [CheckpointType.MEASUREMENT]: 'Measurement',
  [CheckpointType.VISUAL]: 'Visual',
  [CheckpointType.COUNT]: 'Count',
};

export const QCRuleTypeColors: Record<string, string> = {
  BY_SUPPLIER: 'purple',
  BY_PRODUCT: 'blue',
  BY_CATEGORY: 'teal',
  BY_ORIGIN: 'orange',
  BY_VALUE: 'green',
  BY_QUANTITY: 'cyan',
  CUSTOM: 'gray',
};

export const QCInspectionResultLabels: Record<string, string> = {
  PASS: 'Pass',
  FAIL: 'Fail',
  CONDITIONAL: 'Conditional',
  PENDING_REVIEW: 'Pending Review',
};

export const QCInspectionResultColors: Record<string, string> = {
  PASS: 'green',
  FAIL: 'red',
  CONDITIONAL: 'yellow',
  PENDING_REVIEW: 'orange',
};

export const QCCheckpointResultLabels: Record<string, string> = {
  PASS: 'Pass',
  FAIL: 'Fail',
  NA: 'N/A',
  PENDING: 'Pending',
};

export const QCCheckpointResultColors: Record<string, string> = {
  PASS: 'green',
  FAIL: 'red',
  NA: 'gray',
  PENDING: 'yellow',
};

export const QCMethodColors: Record<string, string> = {
  AQL_NORMAL: 'blue',
  AQL_TIGHTENED: 'red',
  AQL_REDUCED: 'green',
  HUNDRED_PERCENT: 'purple',
  SKIP_LOT: 'gray',
  RANDOM_SAMPLE: 'orange',
  FIXED_PERCENTAGE: 'teal',
};

export const HoldActionLabels: Record<string, string> = {
  CREATED: 'Created',
  EXTENDED: 'Extended',
  PARTIALLY_RELEASED: 'Partially Released',
  RELEASED: 'Released',
  CANCELLED: 'Cancelled',
  EXPIRED: 'Expired',
  NOTE_ADDED: 'Note Added',
};

// ============================================================================
// CONSTANTS
// ============================================================================

export const HOLD_STATUS_VALUES = [
  'ACTIVE',
  'RELEASED',
  'PARTIALLY_RELEASED',
  'EXPIRED',
  'CANCELLED',
] as const;

export const HOLD_REASON_VALUES = [
  'QC_FAILURE',
  'PENDING_COA',
  'CUSTOMER_COMPLAINT',
  'RECALL',
  'REGULATORY',
  'DAMAGE',
  'OTHER',
] as const;

export const COA_STATUS_VALUES = ['PENDING', 'VERIFIED', 'REJECTED', 'EXPIRED'] as const;

/**
 * AQL Sample Size Table (ANSI/ASQ Z1.4 General Inspection Level II)
 * Used as default seed data for AQL sampling plans
 */
export const AQL_SAMPLE_SIZE_TABLE_GENERAL_II = [
  { lot_min: 2, lot_max: 8, sample_size: 2, accept: 0, reject: 1 },
  { lot_min: 9, lot_max: 15, sample_size: 3, accept: 0, reject: 1 },
  { lot_min: 16, lot_max: 25, sample_size: 5, accept: 0, reject: 1 },
  { lot_min: 26, lot_max: 50, sample_size: 8, accept: 0, reject: 1 },
  { lot_min: 51, lot_max: 90, sample_size: 13, accept: 1, reject: 2 },
  { lot_min: 91, lot_max: 150, sample_size: 20, accept: 1, reject: 2 },
  { lot_min: 151, lot_max: 280, sample_size: 32, accept: 2, reject: 3 },
  { lot_min: 281, lot_max: 500, sample_size: 50, accept: 3, reject: 4 },
  { lot_min: 501, lot_max: 1200, sample_size: 80, accept: 5, reject: 6 },
  { lot_min: 1201, lot_max: 3200, sample_size: 125, accept: 7, reject: 8 },
  { lot_min: 3201, lot_max: 10000, sample_size: 200, accept: 10, reject: 11 },
  { lot_min: 10001, lot_max: 35000, sample_size: 315, accept: 14, reject: 15 },
  { lot_min: 35001, lot_max: 150000, sample_size: 500, accept: 21, reject: 22 },
  { lot_min: 150001, lot_max: 500000, sample_size: 800, accept: 21, reject: 22 },
] as const;
